import { currentLocale } from '@/lib/i18n'

const localeTag: Record<string, string> = { en: 'en-GB', tr: 'tr-TR', de: 'de-DE' }

function tag(): string {
  return localeTag[currentLocale()] ?? 'en-GB'
}

/** Older certificates were numbered LMS-000001. That prefix is no longer shown. */
export function certificateNumber(value: string | null | undefined): string {
  return (value ?? '').replace(/^LMS-/, '')
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(tag(), { dateStyle: 'medium' }).format(date)
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(tag(), { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

export function formatRelativeDays(value: string | null | undefined): string {
  if (!value) return '—'
  const target = new Date(value)
  if (Number.isNaN(target.getTime())) return '—'

  const days = Math.round((target.getTime() - Date.now()) / 86_400_000)
  return new Intl.RelativeTimeFormat(tag(), { numeric: 'auto' }).format(days, 'day')
}

/** Human-readable duration from seconds: 45s, 12:30, 1:05:00. */
export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '—'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }
  if (minutes > 0) {
    return `${minutes}:${String(secs).padStart(2, '0')}`
  }
  return `${secs}s`
}

/** Coarse duration for course cards, where precision does not matter. */
export function formatMinutes(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return '—'
  if (minutes < 60) return `${minutes} min`

  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat(tag()).format(value)
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `${Math.round(value)}%`
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '—'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const size = bytes / 1024 ** exponent
  return `${size.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?'
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

export function isOverdue(dueDate: string | null | undefined): boolean {
  if (!dueDate) return false
  return new Date(dueDate).getTime() < Date.now()
}
